
import { Component, Ref, Vue } from 'vue-property-decorator'
import AdminUserAcademyList from '@/components/templates/manage/AdminUserAcademyList.vue'
import { UserList } from '@/models/manage/UserList'
import { SelectItem } from '@/models/SelectItem'
import ModalRegistUserAcademy from '@/views/modal/manage/ModalRegistUserAcademy.vue'
import ModalEditUserAcademy from '@/views/modal/manage/ModalEditUserAcademy.vue'
import ModalEditPassword from '@/views/modal/manage/ModalEditPassword.vue'
import { AcademiesApi } from '@/api/methods/manage/AcademiesApi'
import { UserAcademiesApi } from '@/api/methods/manage/UserAcademiesApi'
import { UsersApi } from '@/api/methods/manage/UsersApi'

/**
 * 塾管理者一覧
 */
@Component({
  components: {
    AdminUserAcademyList,
    ModalRegistUserAcademy,
    ModalEditUserAcademy,
    ModalEditPassword,
  },
})
export default class AdminUserAcademies extends Vue {
  /** 塾管理者一覧レイアウト */
  @Ref()
  adminUserAcademyList!: AdminUserAcademyList

  /** 塾管理者登録モーダル */
  @Ref()
  modalRegistUserAcademy!: ModalRegistUserAcademy

  /** 塾管理者編集モーダル */
  @Ref()
  modalEditUserAcademy!: ModalEditUserAcademy

  /** パスワード変更モーダル */
  @Ref()
  modalEditPassword!: ModalEditPassword

  /** 塾フィルターの固定値「全て」 */
  private readonly OPTION_ALL = { value: '', label: '全て' }

  /** 塾フィルターの固定値「選択不可」 */
  private readonly OPTION_DISABLED = { value: 'disabled', label: '選択不可' }

  /** 1ページの最大件数 */
  private readonly MAX_PER_PAGE = 7

  /** 塾管理者一覧 */
  private userAcademyList: UserList[] = []

  /** 塾管理者人数 */
  private userAcademyNumber = 0

  /** 塾フィルターの選択肢 */
  private filterAcademyList: SelectItem[] = []

  /** ページ数 */
  private pageLength = 0

  /** 現在のページ */
  private currentPage = 1

  /**
   * 塾管理者更新後の処理(登録・編集モーダル内で呼び出し)
   *   呼び出し元でローディング処理を行う
   */
  private async afterUpdateUserAcademy(): Promise<void> {
    // 塾管理者一覧取得
    await this.getUserAcademyList()
  }

  /**
   * 初期処理
   */
  private async mounted(): Promise<void> {
    this.$loading.start()

    // 塾一覧取得(フィルター設定)
    await this.getFilterAcademies()

    // 塾管理者一覧取得
    await this.getUserAcademyList()

    this.$loading.complete()
  }

  /**
   * 塾一覧取得(フィルター設定)
   */
  private async getFilterAcademies(): Promise<void> {
    // 塾一覧取得
    const academies = await AcademiesApi.getAcademies()

    // 塾フィルター設定
    this.filterAcademyList = academies.map((academy) => {
      return {
        // 塾IDをコードとして使用
        value: String(academy.id),
        label: academy.name,
      }
    })

    // 選択肢の先頭に「全て」を追加
    this.filterAcademyList.unshift(this.OPTION_ALL)

    // 初期値設定
    this.adminUserAcademyList.setCurrentAcademy(this.filterAcademyList[0].value)
  }

  /**
   * 塾管理者一覧取得
   */
  private async getUserAcademyList(): Promise<void> {
    // オプション引数の設定
    const options = {}
    if (this.currentAcademy()) {
      Object.assign(options, { academyId: Number(this.currentAcademy()) })
    }
    if (this.textInputedSearch()) {
      Object.assign(options, { searchText: this.textInputedSearch() })
    }

    // 塾管理者一覧取得
    const userAcademies = await UserAcademiesApi.getUserAcademies(this.currentPage, this.MAX_PER_PAGE, options)

    // 塾管理者が存在しない場合、終了
    if (userAcademies.userAcademies.length === 0) {
      this.userAcademyList = []
      return
    }

    // 塾管理者の人数を設定
    this.userAcademyNumber = userAcademies.totalCount

    // 取得対象の合計数から、全体のページ数を算出
    const length = userAcademies.totalCount
    const divideNumber = Math.floor(length / this.MAX_PER_PAGE)
    this.pageLength = length % this.MAX_PER_PAGE ? divideNumber + 1 : divideNumber

    // 塾管理者テーブル用データ設定
    this.userAcademyList = userAcademies.userAcademies.map((userAcademy) => {
      return {
        userId: userAcademy.userId,
        nickname: userAcademy.nickname,
        loginId: userAcademy.loginId,
        academyName: userAcademy.academyName,
        avatarIconCode: userAcademy.avatarIconCode,
      }
    })
  }

  /**
   * 選択中の塾
   *
   * @returns 塾コード(ID)
   */
  private currentAcademy(): string | null {
    return this.adminUserAcademyList.currentAcademy()
  }

  /**
   * 塾名検索欄の入力文字列
   *
   * @returns 検索文字列
   */
  private textInputedSearch(): string {
    return this.adminUserAcademyList.textInputedSearch()
  }

  /**
   * ユーザ登録ボタン押下時のイベント
   */
  private eventClickCreateUserButton(): void {
    // モーダルを開く
    this.modalRegistUserAcademy.show()
  }

  /**
   * フィルター変更時のイベント
   */
  private async changeFilter(): Promise<void> {
    this.$loading.start()

    // 現在のページを初期値に戻す
    this.currentPage = 1

    // 塾管理者一覧再取得
    await this.getUserAcademyList()

    this.$loading.complete()
  }

  /**
   * 塾管理者氏名検索
   */
  private async searchUserAcademy(): Promise<void> {
    this.$loading.start()

    // 現在のページを初期値に戻す
    this.currentPage = 1

    // 塾管理者一覧再取得
    await this.getUserAcademyList()

    this.$loading.complete()
  }

  /**
   * 削除ボタン押下時のイベント
   *
   * @param userId ユーザID
   */
  private async eventClickDeleteButton(userId: number): Promise<void> {
    // 削除前確認
    if (!confirm('ユーザを削除しますか？')) {
      return
    }

    this.$loading.start()

    // ユーザ削除
    await UsersApi.deleteUser(userId)

    // 塾管理者一覧再取得
    await this.getUserAcademyList()

    this.$loading.complete()
  }

  /**
   * ユーザデータ編集ボタン押下時のイベント
   *
   * @param userId ユーザID
   */
  private async eventClickEditUserDataButton(userId: number): Promise<void> {
    // モーダルを開く
    await this.modalEditUserAcademy.show(userId)
  }

  /**
   * パスワード編集ボタン押下時のイベント
   *
   * @param userId ユーザID
   */
  private eventClickEditPasswordButton(userId: number): void {
    // モーダルを開く
    this.modalEditPassword.show(userId)
  }

  /**
   * ページリンク押下時の処理
   *
   * @param value ページ番号/戻る/進む
   */
  private async changePage(value: number): Promise<void> {
    this.$loading.start()

    // 現在のページを設定
    this.currentPage = value

    await this.getUserAcademyList()

    this.$loading.complete()
  }
}
